/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

// api_helper.js
import { KEY_ACCESS_TOKEN, store, retrieve } from "../utils/storageHelper";

/**
 * Updated APIHelper for "session + oauth token required" flow.
 *
 * Behavior:
 *  - Ensures an access token is present (hydrates from storage or fetches a new one).
 *  - Always sends credentials: 'include' so the browser session cookie is sent.
 *  - Adds X-CSRFToken header for unsafe methods (reads csrftoken cookie).
 *  - Attaches Authorization: Bearer <token> header to every request.
 *  - On 401:
 *      - If server indicates session_expired -> throw { type: 'session_expired', detail }
 *      - If server indicates oauth_token_invalid -> try refresh token once, retry, else throw { type: 'oauth_token_invalid', detail }
 *  - On AbortError, rethrows so callers can detect cancellations.
 */
class APIHelper {
  constructor({ apiUrl, tokenUrl, clientId, clientSecret }) {
    if (APIHelper.instance) {
      return APIHelper.instance;
    }

    this.apiUrl = apiUrl.replace(/\/$/, "");
    this.tokenUrl = tokenUrl;
    this.clientId = clientId;
    this.clientSecret = clientSecret;

    // In-memory cache; will be hydrated from localStorage when needed
    this.accessToken = null;
    this.tokenExpiry = 0;

    // Internal promise used to serialize token fetch / refresh
    this._tokenPromise = null;

    APIHelper.instance = this;
  }

  // ---------------- cookie helper ----------------
  static _getCookie(name) {
    if (typeof document === "undefined") return null;
    const cookie = document.cookie.split(";").map(s => s.trim()).find(c => c.startsWith(name + "="));
    if (!cookie) return null;
    return decodeURIComponent(cookie.split("=")[1]);
  }

  // ---------------- token storage helpers ----------------
  _hydrateTokenFromStorage() {
    const stored = retrieve(KEY_ACCESS_TOKEN);
    if (stored && typeof stored === "object") {
      // If accessToken is a blank string, treat it as "no token"
      if (stored.accessToken && stored.accessToken !== "") {
        this.accessToken = stored.accessToken;
        this.tokenExpiry = stored.tokenExpiry || 0;
      } else {
        // explicit blank means "no token stored" — ensure in-memory is empty
        this.accessToken = null;
        this.tokenExpiry = 0;
      }
    } else {
      const simple = retrieve("access_token"); // if you keep a plain key
      if (simple && typeof simple === "string" && simple !== "") {
        this.accessToken = simple;
        // we don't know expiry for simple key; force fetch on next use
        this.tokenExpiry = 0;
      } else {
        this.accessToken = null;
        this.tokenExpiry = 0;
      }
    }
  }

  _persistTokenToStorage() {
    if (!this.accessToken || !this.tokenExpiry) return;
    store(KEY_ACCESS_TOKEN, {
      accessToken: this.accessToken,
      tokenExpiry: this.tokenExpiry,
    });
  }

  // ---------------- token fetch (serializes via this._tokenPromise) ----------------
  async fetchToken() {
    if (this._tokenPromise) return this._tokenPromise;

    const p = (async () => {
      if (!this.tokenUrl || !this.clientId || !this.clientSecret) {
        this._tokenPromise = null;
        throw new Error("Missing tokenUrl/clientId/clientSecret for token fetch");
      }

      const resp = await fetch(this.tokenUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          grant_type: "client_credentials",
          client_id: this.clientId,
          client_secret: this.clientSecret,
        }).toString(),
      });

      if (!resp.ok) {
        this._tokenPromise = null;
        // bubble up the error with response attached where possible
        const text = await resp.text().catch(() => "");
        const err = new Error("Failed to fetch access token");
        err.response = resp;
        err.body = text;
        throw err;
      }

      const data = await resp.json();
      this.accessToken = data.access_token;
      const expiresIn = data.expires_in || 3600;
      this.tokenExpiry = Date.now() + expiresIn * 1000 - 30000; // 30s buffer
      this._persistTokenToStorage();
      this._tokenPromise = null;
      return this.accessToken;
    })();

    this._tokenPromise = p;
    return p;
  }

  // ---------------- getToken (hydrate or fetch if expired) ----------------
  async getToken() {
    if (!this.accessToken && !this.tokenExpiry) {
      this._hydrateTokenFromStorage();
    }

    const noToken = !this.accessToken || this.accessToken === "" || !this.tokenExpiry || Date.now() >= this.tokenExpiry;
    if (noToken) {
      await this.fetchToken();
    }

    return this.accessToken;
  }

  // ---------------- auth header helper (used by XHR-based uploads) ----------------
  /**
   * Returns the Authorization + X-CSRFToken headers needed to call the Django
   * backend directly from an XMLHttpRequest (e.g. for upload progress tracking).
   * Ensures the OAuth token is valid before returning.
   */
  async getAuthHeaders() {
    let token;
    try {
      token = await this.getToken();
    } catch {
      token = this.accessToken || '';
    }
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const csrf = APIHelper._getCookie('csrftoken');
    if (csrf) headers['X-CSRFToken'] = csrf;
    return headers;
  }

  // ---------------- core apiCall ----------------
  /**
   * Make an API call sending both session cookie and Authorization header.
   * - path: relative or absolute URL
   * - options: fetch-like options; may contain `signal` for AbortController
   * - retry: boolean to attempt single oauth-token refresh on oauth_token_invalid
   *
   * Throws:
   * - AbortError (rethrows)
   * - { type: 'session_expired', detail }  when server indicates session invalid
   * - { type: 'oauth_token_invalid', detail } when token invalid and refresh/retry fails
   *
   * Otherwise returns fetch Response object.
   */
  async apiCall(path, options = {}, retry = true) {
    // ensure we have token (may fetch if expired)
    let token;
    // allow callers to suppress session-expired dispatch
    const { suppressSessionEvent = false } = options || {};
    try {
      token = await this.getToken();
    } catch (err) {
      // Token fetch failed. We'll still attempt to call the API without token if you prefer,
      // but since server requires both, surface a clear error.
      const e = new Error("Failed to obtain access token");
      e.cause = err;
      throw e;
    }

    const url = path.startsWith("http") ? path : `${this.apiUrl}${path}`;

    // Build headers: copy user headers but don't mutate input
    const baseHeaders = { ...(options.headers || {}) };

    // If non-GET/HEAD add X-CSRFToken if not present
    const method = ((options.method || "GET") + "").toUpperCase();
    if (method !== "GET" && method !== "HEAD") {
      if (!Object.keys(baseHeaders).some(k => k.toLowerCase() === "x-csrftoken")) {
        const csrf = APIHelper._getCookie("csrftoken");
        if (csrf) baseHeaders["X-CSRFToken"] = csrf;
      }
      if (!baseHeaders["Content-Type"] && !(options.body instanceof FormData)) {
        // default content-type when body present and not FormData
        baseHeaders["Content-Type"] = "application/json";
      }
    }

    // Attach Authorization header
    baseHeaders["Authorization"] = `Bearer ${token}`;

    // Build fetch options (include credentials)
    const fetchOptions = { ...options, headers: baseHeaders, credentials: "include" };
    if (options.signal) fetchOptions.signal = options.signal;
    delete fetchOptions.suppressSessionEvent;

    // Helper to parse error detail from JSON body if present
    const _readErrorDetail = async (resp) => {
      const txt = await resp.text().catch(() => "");
      if (!txt) return null;
      try {
        return JSON.parse(txt);
      } catch (e) {
        return txt;
      }
    };

    // Perform the request
    let resp;
    try {
      resp = await fetch(url, fetchOptions);
    } catch (err) {
      if (err && err.name === "AbortError") throw err;
      throw err;
    }

    // If 401: inspect response to see if session_expired or oauth_token_invalid
    if (resp.status === 401 || resp.status === 403) {
      const detail = await _readErrorDetail(resp);
      // support both object detail { detail: 'session_expired', ... } or string
      const reason = detail && (detail.detail || (typeof detail === "string" ? detail : null));

      // If session expired -> surface session_expired error (do NOT retry token)
      if (reason === "session_expired" || (detail && detail.reason === "session_invalid_or_expired")) {
        const err = new Error("Session expired");
        err.type = "session_expired";
        err.detail = detail;
        const eventDetail = err.detail || { title: "Session expired", message: "Login again to continue." };
        if (!suppressSessionEvent) {
          try {
            window.dispatchEvent(new CustomEvent("session-expired", { detail }));
          } catch (e) { /* ignore */ }
        }
        throw err;
      }

      // Any 401 that isn't a session problem means the bearer token was
      // rejected — expired, revoked, or dropped server-side while our cached
      // copy still looked valid by tokenExpiry alone. django-oauth-toolkit
      // reports all of these with a generic "Authentication credentials were
      // not provided." body; the specific invalid_token reason lives only in
      // the WWW-Authenticate header, which the browser will not expose to JS
      // cross-origin. So the 401 status itself is the only reliable signal.
      const oauthInvalid =
        resp.status === 401 ||
        reason === "oauth_token_invalid" ||
        (detail && detail.reason === "invalid_or_expired_oauth_token");

      if (oauthInvalid) {
        if (!retry) {
          const err = new Error("OAuth token invalid");
          err.type = "oauth_token_invalid";
          err.detail = detail;
          throw err;
        }

        // Attempt token refresh
        try {
          await this.fetchToken();
        } catch (tokenErr) {
          const err = new Error("OAuth token invalid and refresh failed");
          err.type = "oauth_token_invalid";
          err.detail = detail;
          err.cause = tokenErr;
          throw err;
        }

        // Retry the request once with refreshed token
        const refreshedToken = this.accessToken;
        const headers2 = { ...(options.headers || {}) };

        // preserve CSRF header and content-type if previously set
        if (method !== "GET" && method !== "HEAD") {
          const csrf = APIHelper._getCookie("csrftoken");
          if (csrf && !Object.keys(headers2).some(k => k.toLowerCase() === "x-csrftoken")) {
            headers2["X-CSRFToken"] = csrf;
          }
          if (!headers2["Content-Type"] && !(options.body instanceof FormData)) {
            headers2["Content-Type"] = "application/json";
          }
        }
        headers2["Authorization"] = `Bearer ${refreshedToken}`;

        const fetchOptions2 = { ...options, headers: headers2, credentials: "include" };
        if (options.signal) fetchOptions2.signal = options.signal;

        try {
          resp = await fetch(url, fetchOptions2);
        } catch (err) {
          if (err && err.name === "AbortError") throw err;
          throw err;
        }

        // If retry returned 401 again -> surface oauth_token_invalid
        if (resp.status === 401) {
          const detail2 = await _readErrorDetail(resp);
          const err = new Error("OAuth token invalid after refresh");
          err.type = "oauth_token_invalid";
          err.detail = detail2;
          throw err;
        }

        // else fall through and return resp
        return resp;
      }

      // Other 401s: just throw generic AuthenticationFailed-style error with details
      const err = new Error("Unauthorized");
      err.type = "unauthorized";
      err.detail = detail;
      throw err;
    }

    // If 403: could be CSRF failure or permission denied — read body and surface
    if (resp.status === 403) {
      const detail = await _readErrorDetail(resp);
      const err = new Error("Forbidden");
      err.type = "forbidden";
      err.detail = detail;
      throw err;
    }

    // Otherwise return response (success or other non-auth errors)
    return resp;
  }
}

export default APIHelper;
